import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faCode,
  faTag,
  faCheck,
  faTimes,
  faSearch,
  faArrowLeft,
  faArrowRight,
  faClipboard,
  faSpinner,
  faDownload,
  faEye,
  faPlay,
  faStop,
  faCog,
  faChevronLeft,
  faChevronRight,
  faFastBackward,
  faFastForward,
} from "@fortawesome/free-solid-svg-icons";
import Markdown from "react-markdown";

const API_URL =
  "https://docminer-ts-ms-gwbzd6h2gddscrgw.francecentral-01.azurewebsites.net";
const fetchConfig = {
  credentials: "include",
  headers: { "Content-Type": "application/json" },
};

const ForceDocminer = () => {
  const { id } = useParams();
  const location = useLocation();

  // Main workflow states
  const [currentStep, setCurrentStep] = useState(1); // 1: Input, 2: Review, 3: Label, 4: Structure
  const [pdfUrl, setPdfUrl] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sessionStats, setSessionStats] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Document content with infinite scroll
  const [allLines, setAllLines] = useState([]); // All loaded lines
  const [displayedLines, setDisplayedLines] = useState([]); // Currently visible lines
  const [currentLoadedPage, setCurrentLoadedPage] = useState(0);
  const [hasMoreLines, setHasMoreLines] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [itemsPerLoad, setItemsPerLoad] = useState(500); // Lines to load per batch

  // Search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTotalResults, setSearchTotalResults] = useState(0);

  // Labeling system
  const [selectedLines, setSelectedLines] = useState(new Set());
  const [currentLabel, setCurrentLabel] = useState("doc_title");
  const [lineLabels, setLineLabels] = useState({});
  const [labelStats, setLabelStats] = useState({});

  // Document structure
  const [isStructuring, setIsStructuring] = useState(false);
  const [structureResult, setStructureResult] = useState(null);

  const contentRef = useRef(null);
  const observerRef = useRef(null);
  const loadTriggerRef = useRef(null);

  // Enhanced label options with better colors and descriptions
  const labelOptions = [
    {
      value: "doc_title",
      label: "Document Title",
      color: "#3b82f6",
      description: "Main document title or heading",
    },
    {
      value: "chapter_title",
      label: "Chapter Title",
      color: "#f59e0b",
      description: "Chapter, section, or subsection titles",
    },
    {
      value: "metadata",
      label: "Metadata",
      color: "#10b981",
      description: "Authors, dates, page numbers, etc.",
    },
  ];

  const getLabelColor = (label) => {
    const option = labelOptions.find((opt) => opt.value === label);
    return option ? option.color : "#6b7280";
  };

  // Initialize from sessionStorage
  useEffect(() => {
    const storedPdfUrl = sessionStorage.getItem("docminer_pdf_url");
    if (storedPdfUrl) {
      setPdfUrl(storedPdfUrl);
      sessionStorage.removeItem("docminer_pdf_url");
    }
  }, []);

  // Auto-process PDF when URL is available
  useEffect(() => {
    if (pdfUrl && currentStep === 1) {
      handleProcessPdf();
    }
  }, [pdfUrl]);

  // Setup infinite scroll observer
  useEffect(() => {
    if (!loadTriggerRef.current || isSearchActive) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreLines && !isLoadingMore) {
          loadMoreLines();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(loadTriggerRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMoreLines, isLoadingMore, isSearchActive]);

  // Load initial content when session is ready
  useEffect(() => {
    if (sessionId && currentStep >= 2 && !isSearchActive) {
      resetAndLoadInitialContent();
    }
  }, [sessionId, currentStep, isSearchActive]);

  // Update label statistics
  useEffect(() => {
    const stats = {};
    labelOptions.forEach((option) => {
      stats[option.value] = Object.values(lineLabels).filter(
        (label) => label === option.value
      ).length;
    });
    setLabelStats(stats);
  }, [lineLabels]);

  /**
   * Reset content and load initial batch
   */
  const resetAndLoadInitialContent = async () => {
    setAllLines([]);
    setDisplayedLines([]);
    setCurrentLoadedPage(0);
    setHasMoreLines(true);
    await loadMoreLines(true);
  };

  /**
   * Load more lines (infinite scroll)
   */
  const loadMoreLines = async (isInitial = false) => {
    if (!sessionId || isLoadingMore || (!hasMoreLines && !isInitial)) return;

    setIsLoadingMore(true);
    const nextPage = isInitial ? 1 : currentLoadedPage + 1;

    try {
      const response = await fetch(
        `${API_URL}/api/v1/sessions/${sessionId}/lines?page=${nextPage}&itemsPerPage=${itemsPerLoad}`,
        { ...fetchConfig, method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Failed to load content");
      }

      const data = await response.json();
      const newLines = data.data.map((line, index) => ({
        text: line.text,
        globalIndex: (nextPage - 1) * itemsPerLoad + index,
      }));

      if (isInitial) {
        setAllLines(newLines);
        setDisplayedLines(newLines);
      } else {
        setAllLines((prev) => [...prev, ...newLines]);
        setDisplayedLines((prev) => [...prev, ...newLines]);
      }

      setCurrentLoadedPage(nextPage);
      setHasMoreLines(
        data.data.length === itemsPerLoad && nextPage < data.totalPages
      );

      console.log(`📄 Loaded page ${nextPage}: ${newLines.length} lines`);
    } catch (error) {
      console.error("Error loading more lines:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  /**
   * STEP 1: Process PDF and create session
   */
  const handleProcessPdf = async () => {
    if (!pdfUrl.trim()) {
      alert("Please enter a valid PDF URL");
      return;
    }

    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/sessions`, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify({ pdfUrl: pdfUrl.trim() }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to process PDF");
      }

      const data = await response.json();
      setSessionId(data.sessionId);
      setSessionStats({
        totalLines: data.totalLines,
        totalChars: data.totalChars,
        actualPages: data.totalPages,
      });

      // Reset state and move to next step
      setLineLabels({});
      setSelectedLines(new Set());
      setAllLines([]);
      setDisplayedLines([]);
      setCurrentLoadedPage(0);
      setHasMoreLines(true);
      setIsSearchActive(false);
      setSearchTerm("");
      setStructureResult(null);
      setCurrentStep(2);

      console.log(
        `✅ Session created: ${data.sessionId} (${data.totalLines} lines)`
      );
    } catch (error) {
      console.error("Error processing PDF:", error);
      alert(`Failed to process PDF: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Enhanced search functionality
   */
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setIsSearchActive(false);
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setDisplayedLines(allLines);
      return;
    }

    if (!sessionId) {
      alert("No document loaded. Please process a PDF first.");
      return;
    }

    setIsSearching(true);
    try {
      // Load more search results if needed
      let allSearchResults = [];
      let currentSearchPage = 1;
      let hasMoreSearchResults = true;

      while (hasMoreSearchResults && allSearchResults.length < 1000) {
        const response = await fetch(
          `${API_URL}/api/v1/sessions/${sessionId}/search`,
          {
            ...fetchConfig,
            method: "POST",
            body: JSON.stringify({
              searchTerm: searchTerm.trim(),
              page: currentSearchPage,
              itemsPerPage: 200,
              fuzzy: false,
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Search failed");
        }

        const data = await response.json();
        allSearchResults = [...allSearchResults, ...data.data];
        hasMoreSearchResults =
          data.data.length === 200 && currentSearchPage < data.totalPages;
        currentSearchPage++;
      }

      setSearchResults(allSearchResults);
      setSearchTotalResults(allSearchResults.length);
      setIsSearchActive(true);
      setCurrentSearchIndex(allSearchResults.length > 0 ? 0 : -1);

      // Display search results
      const searchDisplayLines = allSearchResults.map((result, index) => ({
        text: result.line.text,
        globalIndex: result.lineIndex,
        searchIndex: index,
      }));
      setDisplayedLines(searchDisplayLines);

      if (allSearchResults.length > 0) {
        setTimeout(() => scrollToLine(0), 100);
      }

      console.log(`🔍 Found ${allSearchResults.length} search results`);
    } catch (error) {
      console.error("Search error:", error);
      alert(`Search failed: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  /**
   * Navigate search results
   */
  const navigateSearchResults = (direction) => {
    if (searchResults.length === 0) return;

    let newIndex;
    if (direction === "next") {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex =
        (currentSearchIndex - 1 + searchResults.length) % searchResults.length;
    }

    setCurrentSearchIndex(newIndex);
    scrollToLine(newIndex);
  };

  /**
   * Line selection management
   */
  const toggleLineSelection = (index) => {
    const newSelection = new Set(selectedLines);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedLines(newSelection);
  };

  const handleLineClick = (index, event) => {
    if (event.shiftKey && selectedLines.size > 0) {
      // Range selection
      const selectedArray = Array.from(selectedLines);
      const lastSelected = Math.max(...selectedArray);
      const start = Math.min(lastSelected, index);
      const end = Math.max(lastSelected, index);

      const newSelection = new Set(selectedLines);
      for (let i = start; i <= end; i++) {
        newSelection.add(i);
      }
      setSelectedLines(newSelection);
    } else if (event.ctrlKey || event.metaKey) {
      // Multi-selection
      toggleLineSelection(index);
    } else {
      // Single selection
      setSelectedLines(new Set([index]));
    }
  };

  /**
   * Apply labels to selected lines
   */
  const applyLabelToSelected = () => {
    if (selectedLines.size === 0) {
      alert("Please select lines to label first");
      return;
    }

    const updatedLabels = { ...lineLabels };

    selectedLines.forEach((index) => {
      const lineData = displayedLines[index];
      if (lineData && lineData.globalIndex !== undefined) {
        updatedLabels[lineData.globalIndex] = currentLabel;
      }
    });

    setLineLabels(updatedLabels);
    setSelectedLines(new Set());
  };

  /**
   * Process document structure
   */
  const processDocumentStructure = async () => {
    if (!sessionId) {
      alert("No active session");
      return;
    }

    if (Object.keys(lineLabels).length === 0) {
      alert("Please label some lines first");
      return;
    }

    const forcedLabels = {
      doc_title: [],
      chapter_title: [],
      metadata: [],
    };

    Object.entries(lineLabels).forEach(([lineIdx, labelType]) => {
      if (forcedLabels[labelType]) {
        forcedLabels[labelType].push(parseInt(lineIdx));
      }
    });

    setIsStructuring(true);
    try {
      const response = await fetch(
        `${API_URL}/api/v1/sessions/${sessionId}/structure`,
        {
          ...fetchConfig,
          method: "POST",
          body: JSON.stringify({ forcedLabels }),
        }
      );

      if (!response.ok) {
        throw new Error("Structure processing failed");
      }

      const result = await response.json();
      setStructureResult(result);
      setCurrentStep(4);

      alert(
        `✅ Document structured successfully!\n\nTitle: ${result.doc_title}\nChapters: ${result.chapters_length}\nPages: ${result.total_pages}`
      );
    } catch (error) {
      console.error("Structure error:", error);
      alert(`Failed to structure document: ${error.message}`);
    } finally {
      setIsStructuring(false);
    }
  };

  /**
   * Utility functions
   */
  const scrollToLine = (lineIndex) => {
    if (!contentRef.current) return;
    const lineElement = contentRef.current.querySelector(
      `[data-line-index="${lineIndex}"]`
    );
    if (lineElement) {
      lineElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const exportLabels = () => {
    const labelExport = { doc_title: [], chapter_title: [], metadata: [] };
    Object.entries(lineLabels).forEach(([lineIdx, labelType]) => {
      if (labelExport[labelType]) {
        labelExport[labelType].push(parseInt(lineIdx));
      }
    });

    navigator.clipboard
      .writeText(JSON.stringify(labelExport, null, 2))
      .then(() => alert("Labels exported to clipboard!"))
      .catch(() => alert("Failed to export labels"));
  };

  const downloadStructuredDocument = () => {
    if (!structureResult) return;
    const dataStr = JSON.stringify(structureResult, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `structured_document_${
      new Date().toISOString().split("T")[0]
    }.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getLineLabel = (lineData) => {
    return lineData?.globalIndex !== undefined
      ? lineLabels[lineData.globalIndex]
      : null;
  };

  return (
    <div className="container mt-4 mb-6">
      <div className="box neumorphic">
        {/* Header with workflow steps */}
        <div className="level mb-4">
          <div className="level-left">
            <div className="level-item">
              <h1 className="title is-4">ForceDocminer</h1>
            </div>
          </div>
          <div className="level-right">
            <div className="level-item">
              <div className="steps-indicator">
                {[
                  { num: 1, label: "Load PDF", icon: faFileAlt },
                  { num: 2, label: "Review", icon: faEye },
                  { num: 3, label: "Label", icon: faTag },
                  { num: 4, label: "Structure", icon: faCode },
                ].map((step) => (
                  <span
                    key={step.num}
                    className={`step-item ${
                      currentStep >= step.num ? "is-active" : ""
                    } ${currentStep === step.num ? "is-current" : ""}`}
                  >
                    <FontAwesomeIcon icon={step.icon} />
                    <span className="ml-1">{step.label}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* STEP 1: PDF Input */}
        {currentStep === 1 && (
          <div className="box neumorphic p-5">
            <h2 className="title is-5 mb-4">
              <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
              Load PDF Document
            </h2>
            <div className="field">
              <label className="label">PDF URL</label>
              <div className="field has-addons">
                <div className="control is-expanded">
                  <input
                    className="input neumorphic-inset"
                    type="url"
                    placeholder="https://example.com/document.pdf"
                    value={pdfUrl}
                    onChange={(e) => setPdfUrl(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleProcessPdf()}
                  />
                </div>
                <div className="control">
                  <button
                    className={`button is-primary ${
                      isProcessing ? "is-loading" : ""
                    }`}
                    onClick={handleProcessPdf}
                    disabled={isProcessing || !pdfUrl.trim()}
                  >
                    <FontAwesomeIcon icon={faPlay} className="mr-2" />
                    Process PDF
                  </button>
                </div>
              </div>
              <p className="help">
                Enter the URL of a PDF document to begin processing
              </p>
            </div>
          </div>
        )}

        {/* Document Stats Bar */}
        {sessionStats && currentStep >= 2 && (
          <div className="notification is-light is-primary p-3 mb-4">
            <div className="level is-mobile">
              <div className="level-left">
                <div className="level-item">
                  <div>
                    <strong>Session:</strong> {sessionId?.substring(0, 8)}...
                  </div>
                </div>
                <div className="level-item">
                  <div className="tags has-addons">
                    <span className="tag">Lines</span>
                    <span className="tag is-info">
                      {sessionStats.totalLines?.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="level-item">
                  <div className="tags has-addons">
                    <span className="tag">Loaded</span>
                    <span className="tag is-success">
                      {allLines.length.toLocaleString()}
                    </span>
                  </div>
                </div>
                {Object.keys(labelStats).some((key) => labelStats[key] > 0) && (
                  <div className="level-item">
                    <div className="tags">
                      {labelOptions.map(
                        (option) =>
                          labelStats[option.value] > 0 && (
                            <span
                              key={option.value}
                              className="tag"
                              style={{
                                backgroundColor: option.color,
                                color: "white",
                              }}
                            >
                              {option.label}: {labelStats[option.value]}
                            </span>
                          )
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="level-right">
                <div className="level-item">
                  <div className="buttons">
                    {currentStep === 2 && (
                      <button
                        className="button is-success"
                        onClick={() => setCurrentStep(3)}
                      >
                        <FontAwesomeIcon icon={faTag} className="mr-2" />
                        Start Labeling
                      </button>
                    )}
                    {currentStep === 3 && (
                      <button
                        className="button is-light"
                        onClick={() => setCurrentStep(2)}
                      >
                        <FontAwesomeIcon icon={faEye} className="mr-2" />
                        Review Only
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 & 3: Content Review and Labeling */}
        {currentStep >= 2 && currentStep <= 3 && (
          <>
            {/* Search Bar */}
            <div className="box neumorphic p-3 mb-4">
              <div className="level is-mobile">
                <div className="level-left">
                  <div className="level-item">
                    <div className="field has-addons">
                      <div className="control has-icons-left">
                        <input
                          className="input"
                          type="text"
                          placeholder="Search entire document..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleSearch()
                          }
                        />
                        <span className="icon is-small is-left">
                          <FontAwesomeIcon icon={faSearch} />
                        </span>
                      </div>
                      <div className="control">
                        <button
                          className={`button ${
                            isSearching ? "is-loading" : ""
                          }`}
                          onClick={handleSearch}
                        >
                          {isSearchActive ? "Clear" : "Search"}
                        </button>
                      </div>
                      {isSearchActive && searchResults.length > 0 && (
                        <>
                          <div className="control">
                            <button
                              className="button"
                              onClick={() => navigateSearchResults("prev")}
                            >
                              <FontAwesomeIcon icon={faArrowLeft} />
                            </button>
                          </div>
                          <div className="control">
                            <span className="button is-static">
                              {currentSearchIndex + 1} of {searchResults.length}
                            </span>
                          </div>
                          <div className="control">
                            <button
                              className="button"
                              onClick={() => navigateSearchResults("next")}
                            >
                              <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="level-right">
                  <div className="level-item">
                    <div className="field has-addons">
                      <div className="control">
                        <span className="button is-small is-static">
                          Load per batch:
                        </span>
                      </div>
                      <div className="control">
                        <div className="select is-small">
                          <select
                            value={itemsPerLoad}
                            onChange={(e) =>
                              setItemsPerLoad(parseInt(e.target.value))
                            }
                          >
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Labeling Controls - Always show when in step 3 */}
            {currentStep === 3 && (
              <div className="box neumorphic p-4 mb-4">
                <div className="notification is-info is-light mb-4">
                  <div className="level is-mobile">
                    <div className="level-left">
                      <div className="level-item">
                        <strong>📝 Labeling Mode Active</strong>
                      </div>
                    </div>
                    <div className="level-right">
                      <div className="level-item">
                        <span className="tag is-info">
                          {selectedLines.size} line
                          {selectedLines.size !== 1 ? "s" : ""} selected
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="columns">
                  <div className="column is-4">
                    <div className="field">
                      <label className="label">Label Type</label>
                      <div className="control">
                        <div className="select is-fullwidth">
                          <select
                            value={currentLabel}
                            onChange={(e) => setCurrentLabel(e.target.value)}
                          >
                            {labelOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label} - {option.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="help">
                        Current label:{" "}
                        <span
                          style={{
                            color: getLabelColor(currentLabel),
                            fontWeight: "bold",
                          }}
                        >
                          {
                            labelOptions.find(
                              (opt) => opt.value === currentLabel
                            )?.label
                          }
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="column is-4">
                    <div className="field">
                      <label className="label">Actions</label>
                      <div className="buttons">
                        <button
                          className="button is-primary"
                          onClick={applyLabelToSelected}
                          disabled={selectedLines.size === 0}
                        >
                          <FontAwesomeIcon icon={faTag} className="mr-2" />
                          Apply Label ({selectedLines.size})
                        </button>
                        <button
                          className="button"
                          onClick={() => setSelectedLines(new Set())}
                          disabled={selectedLines.size === 0}
                        >
                          <FontAwesomeIcon icon={faTimes} className="mr-2" />
                          Clear Selection
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="column is-4">
                    <div className="field">
                      <label className="label">Document Processing</label>
                      <div className="buttons">
                        <button
                          className={`button is-success ${
                            isStructuring ? "is-loading" : ""
                          }`}
                          onClick={processDocumentStructure}
                          disabled={
                            Object.keys(lineLabels).length === 0 ||
                            isStructuring
                          }
                        >
                          <FontAwesomeIcon icon={faCode} className="mr-2" />
                          Structure Document
                        </button>
                        <button
                          className="button"
                          onClick={exportLabels}
                          disabled={Object.keys(lineLabels).length === 0}
                        >
                          <FontAwesomeIcon
                            icon={faClipboard}
                            className="mr-2"
                          />
                          Export Labels
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="notification is-light is-info p-3 mt-3">
                  <strong>🎯 How to Label:</strong>
                  <ul className="mt-2 mb-0">
                    <li>
                      <strong>Single click:</strong> Select one line
                    </li>
                    <li>
                      <strong>Ctrl/Cmd + click:</strong> Add/remove lines from
                      selection
                    </li>
                    <li>
                      <strong>Shift + click:</strong> Select range of lines
                    </li>
                    <li>
                      <strong>Apply Label button:</strong> Label all selected
                      lines with the chosen type
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP 4: Structure Results */}
        {currentStep === 4 && structureResult && (
          <div className="box neumorphic p-4 mb-4">
            <h2 className="title is-5 mb-4">
              <FontAwesomeIcon icon={faCheck} className="mr-2" />
              Document Structured Successfully
            </h2>
            <div className="columns">
              <div className="column">
                <div className="content">
                  <h4>Document Title:</h4>
                  <p className="has-text-weight-semibold">
                    {structureResult.doc_title}
                  </p>
                  <h4>Statistics:</h4>
                  <ul>
                    <li>Chapters: {structureResult.chapters_length}</li>
                    <li>Total Pages: {structureResult.total_pages}</li>
                    <li>
                      Processing:{" "}
                      {structureResult.processing_info || "Completed"}
                    </li>
                  </ul>
                </div>
              </div>
              <div className="column is-narrow">
                <button
                  className="button is-success is-large"
                  onClick={downloadStructuredDocument}
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Download JSON
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Display - Infinite Scroll */}
        {currentStep >= 2 && (
          <div className="box neumorphic">
            <div className="level mb-3">
              <div className="level-left">
                <div className="level-item">
                  <h3 className="title is-6 mb-0">
                    Document Content
                    {currentStep === 3 && (
                      <span className="tag is-info ml-2">
                        Click lines to label them
                      </span>
                    )}
                    {(isLoadingMore || isSearching) && (
                      <FontAwesomeIcon icon={faSpinner} spin className="ml-2" />
                    )}
                  </h3>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <span className="tag">
                    {isSearchActive
                      ? `${displayedLines.length} search results`
                      : `${displayedLines.length} of ${
                          sessionStats?.totalLines || 0
                        } lines`}
                  </span>
                </div>
              </div>
            </div>

            <div
              ref={contentRef}
              className="content-panel"
              style={{
                height: "calc(100vh - 350px)",
                overflow: "auto",
                border: "1px solid #e5e5e5",
                borderRadius: "8px",
                padding: "16px",
                cursor: currentStep === 3 ? "crosshair" : "default",
              }}
            >
              <div className="line-container">
                {displayedLines.map((lineData, index) => {
                  const isSelected = selectedLines.has(index);
                  const isCurrentSearch =
                    isSearchActive && currentSearchIndex === index;
                  const lineLabel = getLineLabel(lineData);

                  return (
                    <div
                      key={`line-${lineData.globalIndex}-${index}`}
                      data-line-index={index}
                      className={`content-line ${
                        isSelected ? "is-selected" : ""
                      } ${isCurrentSearch ? "is-current-search" : ""} ${
                        isSearchActive ? "is-search-result" : ""
                      }`}
                      onClick={(e) =>
                        currentStep === 3 && handleLineClick(index, e)
                      }
                      style={{
                        padding: "6px 12px",
                        margin: "2px 0",
                        borderRadius: "4px",
                        cursor: currentStep === 3 ? "pointer" : "default",
                        position: "relative",
                        borderLeft: lineLabel
                          ? `4px solid ${getLabelColor(lineLabel)}`
                          : "none",
                        backgroundColor: isSelected
                          ? "#e3f2fd"
                          : isCurrentSearch
                          ? "#fff59d"
                          : isSearchActive
                          ? "#f3e5f5"
                          : "transparent",
                      }}
                    >
                      <span className="line-number">
                        {lineData.globalIndex + 1}
                      </span>
                      <div className="line-content">
                        {lineData.text ? (
                          <Markdown>{lineData.text}</Markdown>
                        ) : (
                          <span className="empty-line">&nbsp;</span>
                        )}
                      </div>
                      {lineLabel && (
                        <span
                          className="tag is-small line-label"
                          style={{
                            backgroundColor: getLabelColor(lineLabel),
                            color: "white",
                          }}
                        >
                          {
                            labelOptions.find((opt) => opt.value === lineLabel)
                              ?.label
                          }
                        </span>
                      )}
                    </div>
                  );
                })}

                {/* Infinite scroll trigger */}
                {!isSearchActive && hasMoreLines && (
                  <div
                    ref={loadTriggerRef}
                    className="load-trigger"
                    style={{
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "20px 0",
                    }}
                  >
                    {isLoadingMore && (
                      <span className="tag is-loading">
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="mr-2"
                        />
                        Loading more content...
                      </span>
                    )}
                  </div>
                )}

                {displayedLines.length === 0 &&
                  !isLoadingMore &&
                  !isSearching && (
                    <div className="has-text-centered p-6">
                      <p className="has-text-grey">
                        {sessionId
                          ? "No content found."
                          : "Load a PDF document to start."}
                      </p>
                    </div>
                  )}

                {isSearchActive &&
                  displayedLines.length === 0 &&
                  !isSearching && (
                    <div className="has-text-centered p-6">
                      <p className="has-text-grey">
                        No search results found for "{searchTerm}"
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .steps-indicator {
          display: flex;
          gap: 1rem;
        }
        .step-item {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          background: #f5f5f5;
          color: #999;
          transition: all 0.3s ease;
        }
        .step-item.is-active {
          background: #e3f2fd;
          color: #1976d2;
        }
        .step-item.is-current {
          background: #1976d2;
          color: white;
          font-weight: 600;
        }
        .content-line {
          transition: all 0.2s ease;
          ${currentStep === 3 ? "user-select: none;" : ""}
        }
        .content-line:hover {
          background-color: ${currentStep === 3
            ? "#f0f8ff"
            : "#f8f9fa"} !important;
          ${currentStep === 3 ? "transform: translateX(2px);" : ""}
        }
        .content-line.is-selected {
          border: 2px solid #1976d2;
          box-shadow: 0 2px 4px rgba(25, 118, 210, 0.2);
        }
        .content-line.is-current-search {
          box-shadow: 0 0 0 3px #ffc107;
        }
        .line-number {
          position: absolute;
          left: -40px;
          top: 6px;
          font-size: 0.75rem;
          color: #999;
          font-family: monospace;
          user-select: none;
          width: 35px;
          text-align: right;
        }
        .line-content {
          margin-left: 10px;
        }
        .line-content p {
          margin: 0;
        }
        .line-label {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
        }
        .line-container {
          position: relative;
          padding-left: 45px;
        }
        .content-panel {
          background: #fafafa;
        }
        .empty-line {
          color: #ccc;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default ForceDocminer;
